import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';

const API = axios.create({ baseURL: '/api/landed-cost', timeout: 20000 });

const lbl = { fontSize: 12.5, fontWeight: 600, color: 'var(--sf-primary)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'ZAR', 'KES', 'NGN'];

export default function LandedCostEstimator() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.landed-cost.meta_title'] || 'Landed Cost & Shipping Estimator for Opticians',
    description: c['tools.landed-cost.meta_description'] || 'Free landed cost calculator for optical retailers — work out true wholesale cost including international freight, customs duty and local tax before you place an order.',
    keywords: c['tools.landed-cost.meta_keywords'] || 'landed cost calculator, import cost estimator, wholesale shipping calculator, optician tools, freight and duty calculator',
    canonical: `${window.location.origin}/store/tools/landed-cost-estimator`,
  });

  const [countries, setCountries] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [countryId, setCountryId] = useState('');
  const [courierId, setCourierId] = useState('');
  const [wholesale, setWholesale] = useState('5000');
  const [currency, setCurrency] = useState('USD');
  const [pieces, setPieces] = useState('200');
  const [dutyPct, setDutyPct] = useState('10');
  const [taxPct, setTaxPct] = useState('15');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/countries').then(r => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setCourierId('');
    setCouriers([]);
    if (!countryId) return;
    API.get('/couriers', { params: { country_id: countryId } }).then(r => setCouriers(r.data.couriers || [])).catch(() => {});
  }, [countryId]);

  const canEstimate = wholesale && pieces && countryId && courierId;

  useEffect(() => {
    if (!canEstimate) { setResult(null); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    const t = setTimeout(() => {
      API.post('/estimate', {
        wholesale_value: wholesale, currency, total_pieces: pieces,
        country_id: countryId, courier_id: courierId, duty_pct: dutyPct, tax_pct: taxPct,
      }).then(r => { if (!cancelled) setResult(r.data); })
        .catch(e => { if (!cancelled) { setError(e.response?.data?.error || 'Could not calculate estimate'); setResult(null); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [wholesale, currency, pieces, countryId, courierId, dutyPct, taxPct]); // eslint-disable-line

  const money = useMemo(() => (v) => {
    if (v == null) return '—';
    return `${result?.display?.currency || currency} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [result, currency]);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24 };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Landed Cost &amp; Shipping Estimator
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          Work out true wholesale cost — real freight rates, plus your own duty and tax estimate — before you place an international order.
        </p>
      </section>

      {/* CALCULATOR */}
      <section style={{ maxWidth: 1100, margin: '24px auto 0', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) minmax(300px,1.1fr)', gap: 24 }} className="lce-grid">
          {/* INPUTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Order &amp; Shipment</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Wholesale Order Value</label>
                <input type="number" min="0" step="1" value={wholesale} onChange={e => setWholesale(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Currency</label>
                <select value={currency} onChange={e => setCurrency(e.target.value)} style={inp}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Total Pieces (frames)</label>
              <input type="number" min="1" step="1" value={pieces} onChange={e => setPieces(e.target.value)} style={inp} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Destination Country</label>
              <select value={countryId} onChange={e => setCountryId(e.target.value)} style={inp}>
                <option value="">Select country…</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Courier</label>
              <select value={courierId} onChange={e => setCourierId(e.target.value)} style={inp} disabled={!countryId}>
                <option value="">{countryId ? 'Select courier…' : 'Select a country first'}</option>
                {couriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {countryId && couriers.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 4 }}>No courier currently ships to this country.</div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>Est. Customs Duty (%)</label>
                <input type="number" min="0" step="0.5" value={dutyPct} onChange={e => setDutyPct(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Local Sales Tax / VAT (%)</label>
                <input type="number" min="0" step="0.5" value={taxPct} onChange={e => setTaxPct(e.target.value)} style={inp} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 8, lineHeight: 1.6 }}>
              Duty and tax rates vary by country and product classification — enter your own estimate.
              Freight is a real courier rate; wholesale value converts at today's live exchange rate.
            </div>
          </div>

          {/* RESULTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Landed Cost</h3>
            {error && (
              <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 14 }}>
                {error}
              </div>
            )}
            {!error && !result && (
              <div style={{ fontSize: 13, color: 'var(--sf-muted)' }}>
                {canEstimate ? (loading ? 'Calculating…' : 'Enter details to see your estimate.') : 'Fill in the order details to see a live estimate.'}
              </div>
            )}
            {result && (
              <>
                <Row label="Wholesale Value" value={money(result.display.wholesale)} />
                <Row label={`Freight — ${result.courier_name} (${result.weight_kg} kg)`} value={money(result.display.freight)} />
                <Row label={`Customs Duty (${result.duty_pct}%)`} value={money(result.display.duty)} />
                <Row label={`Sales Tax / VAT (${result.tax_pct}%)`} value={money(result.display.tax)} />
                <div style={{ borderTop: '1px solid #eee', margin: '10px 0' }} />
                <div style={{
                  background: 'var(--sf-primary)', color: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 12,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.85 }}>Total Landed Cost</span>
                  <span style={{ fontSize: 22, fontWeight: 800 }}>{money(result.display.total)}</span>
                </div>
                <div style={{
                  background: 'color-mix(in srgb, var(--sf-accent) 14%, #fff)', borderRadius: 10, padding: '12px 16px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sf-primary)' }}>Landed Cost / Piece</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--sf-primary)' }}>{money(result.display.per_unit)}</span>
                </div>
                {result.display.fx_error && (
                  <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 10 }}>
                    Live exchange rate lookup failed — figures shown in INR.
                  </div>
                )}
                <p style={{ fontSize: 11, color: 'var(--sf-muted)', lineHeight: 1.6, marginTop: 16 }}>
                  Freight is calculated from real courier rates to {result.country}. Duty and tax are your own
                  entered estimates. Always confirm exact customs charges with your freight forwarder before ordering.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff', marginTop: 48 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 780px) {
          .lce-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', fontSize: 13.5 }}>
      <span style={{ color: 'var(--sf-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: 'var(--sf-primary)' }}>{value}</span>
    </div>
  );
}
